/**
 * Custom Search Modal - Replace Boost PFS Widget
 * Direct integration with Boost Commerce API
 */
(function() {
  'use strict';

  class CustomSearchModal {
    constructor() {
      this.modal = null;
      this.overlay = null;
      this.searchInput = null;
      this.resultsContainer = null;
      this.isOpen = false;
      this.searchDebounceTimer = null;
      this.API_URL = 'https://services.mybcapps.com/bc-sf-filter/search/suggest';
      this.SHOP = 'minijumbuk.myshopify.com';
      this.RECENT_SEARCHES_KEY = 'minijumbuk_recent_searches';
      this.MAX_RECENT_SEARCHES = 5;

      // Popular search terms (can be customized)
      this.POPULAR_SEARCHES = [
        'quilts',
        'pillows',
        'mattress topper',
        'wool',
        'protector'
      ];
    }

    // Initialize and create modal structure
    init() {
      this.createModal();
      this.attachEventHandlers();
      console.log('✅ Custom Search Modal initialized');
    }

    // Create modal HTML structure
    createModal() {
      // Create overlay
      this.overlay = document.createElement('div');
      this.overlay.className = 'custom-search-overlay';

      // Create modal container
      this.modal = document.createElement('div');
      this.modal.className = 'custom-search-modal';

      // Build modal content
      this.modal.innerHTML = `
        <div class="custom-search-header">
          <div class="custom-search-input-wrapper">
            <svg class="custom-search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <input
              type="text"
              class="custom-search-input"
              placeholder="Search products, collections..."
              autocomplete="off"
              aria-label="Search"
            />
          </div>
          <button class="custom-search-close" aria-label="Close search">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="custom-search-body">
          <div class="custom-search-results"></div>
        </div>
      `;

      // Cache element references
      this.searchInput = this.modal.querySelector('.custom-search-input');
      this.resultsContainer = this.modal.querySelector('.custom-search-results');

      // Add to DOM (hidden initially)
      document.body.appendChild(this.overlay);
      document.body.appendChild(this.modal);
    }

    // Attach event handlers
    attachEventHandlers() {
      // Close button
      const closeBtn = this.modal.querySelector('.custom-search-close');
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });

      // Overlay click
      this.overlay.addEventListener('click', () => {
        this.close();
      });

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Prevent modal click from closing
      this.modal.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      // Search input - debounced
      this.searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();

        // Clear previous timer
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }

        // Debounce search (300ms)
        this.searchDebounceTimer = setTimeout(() => {
          if (query.length >= 2) {
            this.search(query);
          } else if (query.length === 0) {
            this.showEmptyState();
          }
        }, 300);
      });

      // Search on Enter key
      this.searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          const query = e.target.value.trim();
          if (query.length >= 2) {
            if (this.searchDebounceTimer) {
              clearTimeout(this.searchDebounceTimer);
            }
            this.search(query);
          }
        }
      });
    }

    // Open modal
    open() {
      this.isOpen = true;
      this.overlay.classList.add('active');
      this.modal.classList.add('active');
      document.body.classList.add('no-scroll');

      // Focus search input after animation
      setTimeout(() => {
        this.searchInput.focus();
      }, 100);

      // Show initial empty state
      this.showEmptyState();
    }

    // Close modal
    close() {
      this.isOpen = false;
      this.overlay.classList.remove('active');
      this.modal.classList.remove('active');
      document.body.classList.remove('no-scroll');

      // Clear search after animation
      setTimeout(() => {
        this.searchInput.value = '';
        this.resultsContainer.innerHTML = '';
      }, 300);
    }

    // Show empty state with recent searches and popular products
    async showEmptyState() {
      const recentSearches = this.getRecentSearches();

      let html = '';

      // Recent Searches
      if (recentSearches.length > 0) {
        html += '<div class="custom-search-section">';
        html += '<h3 class="custom-search-section-title">Recent Searches</h3>';
        html += '<div class="custom-search-suggestions">';
        recentSearches.forEach(term => {
          html += `
            <button class="custom-search-suggestion-item" data-query="${term}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14z" stroke="currentColor" stroke-width="1.5"/>
                <path d="M12 12l-3-3M8 5v5M8 5L6 7M8 5l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${term}</span>
            </button>
          `;
        });
        html += '</div></div>';
      }

      // Popular Searches
      html += '<div class="custom-search-section">';
      html += '<h3 class="custom-search-section-title">Popular Searches</h3>';
      html += '<div class="custom-search-suggestions">';
      this.POPULAR_SEARCHES.forEach(term => {
        html += `
          <button class="custom-search-suggestion-item" data-query="${term}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1v6l4 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14z" stroke="currentColor" stroke-width="1.5"/>
            </svg>
            <span>${term}</span>
          </button>
        `;
      });
      html += '</div></div>';

      // Load popular products
      html += '<div class="custom-search-section">';
      html += '<h3 class="custom-search-section-title">Popular Products</h3>';
      html += '<div id="popular-products-loading" class="custom-search-loading-inline">Loading...</div>';
      html += '<div id="popular-products-container" class="custom-search-products" style="display:none;"></div>';
      html += '</div>';

      this.resultsContainer.innerHTML = html;

      // Attach click handlers for suggestions
      this.resultsContainer.querySelectorAll('[data-query]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const query = btn.getAttribute('data-query');
          this.searchInput.value = query;
          this.search(query);
        });
      });

      // Load popular products
      this.loadPopularProducts();
    }

    // Load popular products (best selling)
    async loadPopularProducts() {
      try {
        const params = new URLSearchParams({
          shop: this.SHOP,
          q: '*', // Search all
          limit: 8,
          page: 1,
          sort: 'best-selling',
          event_type: 'suggest',
          suggest_types: 'products'
        });

        const apiUrl = `${this.API_URL}?${params.toString()}`;
        const response = await fetch(apiUrl);
        const data = await response.json();

        const loadingEl = document.getElementById('popular-products-loading');
        const containerEl = document.getElementById('popular-products-container');

        if (loadingEl && containerEl) {
          loadingEl.style.display = 'none';
          containerEl.style.display = 'grid';

          if (data.products && data.products.length > 0) {
            let html = '';
            data.products.slice(0, 8).forEach(product => {
              const image = product.images_info?.[0]?.src || product.images?.['1'] || '';
              const price = product.price_min ? `$${product.price_min.toFixed(2)}` : '';
              const comparePrice = product.compare_at_price_min ? `$${product.compare_at_price_min.toFixed(2)}` : '';
              const onSale = comparePrice && product.compare_at_price_min > product.price_min;

              html += `
                <a href="/products/${product.handle}" class="custom-search-product-item">
                  <div class="custom-search-product-image">
                    ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` : '<div class="custom-search-product-no-image">No Image</div>'}
                    ${!product.available ? '<span class="custom-search-badge sold-out">Sold Out</span>' : ''}
                    ${onSale ? '<span class="custom-search-badge sale">Sale</span>' : ''}
                  </div>
                  <div class="custom-search-product-info">
                    <h4 class="custom-search-product-title">${product.title}</h4>
                    <div class="custom-search-product-price">
                      ${onSale ? `<span class="custom-search-price-compare">${comparePrice}</span>` : ''}
                      <span class="custom-search-price">${price}</span>
                    </div>
                  </div>
                </a>
              `;
            });
            containerEl.innerHTML = html;
          } else {
            containerEl.innerHTML = '<p class="custom-search-empty-text">No popular products available</p>';
          }
        }
      } catch (error) {
        console.error('Failed to load popular products:', error);
        const loadingEl = document.getElementById('popular-products-loading');
        if (loadingEl) {
          loadingEl.innerHTML = 'Failed to load products';
        }
      }
    }

    // Get recent searches from localStorage
    getRecentSearches() {
      try {
        const searches = localStorage.getItem(this.RECENT_SEARCHES_KEY);
        return searches ? JSON.parse(searches) : [];
      } catch (error) {
        return [];
      }
    }

    // Save search to recent searches
    saveRecentSearch(query) {
      try {
        let searches = this.getRecentSearches();

        // Remove if already exists
        searches = searches.filter(term => term.toLowerCase() !== query.toLowerCase());

        // Add to beginning
        searches.unshift(query);

        // Keep only MAX_RECENT_SEARCHES
        searches = searches.slice(0, this.MAX_RECENT_SEARCHES);

        localStorage.setItem(this.RECENT_SEARCHES_KEY, JSON.stringify(searches));
      } catch (error) {
        console.error('Failed to save recent search:', error);
      }
    }

    // Show loading state
    showLoading() {
      this.resultsContainer.innerHTML = `
        <div class="custom-search-loading">
          <div class="custom-search-spinner"></div>
          <p>Searching...</p>
        </div>
      `;
    }

    // Call Boost API
    async search(query) {
      this.showLoading();

      // Save to recent searches
      this.saveRecentSearch(query);

      const params = new URLSearchParams({
        shop: this.SHOP,
        q: query,
        limit: 20,
        page: 1,
        event_type: 'suggest',
        suggest_types: 'products,pages,collections,suggestions,did_you_mean',
        suggest_fields: 'title,product_type,vendor,tags',
        suggestion_limit: 5,
        dym_limit: 3
      });

      const apiUrl = `${this.API_URL}?${params.toString()}`;

      console.log('🔍 Searching:', query);

      try {
        const response = await fetch(apiUrl);
        const data = await response.json();

        console.log('✅ Search results:', data);
        this.displayResults(query, data);
      } catch (error) {
        console.error('❌ Search error:', error);
        this.showError(error.message);
      }
    }

    // Display search results
    displayResults(query, data) {
      let html = '';

      // Check if all empty
      if (data.all_empty || (!data.products?.length && !data.collections?.length && !data.pages?.length)) {
        html = `
          <div class="custom-search-no-results">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2" opacity="0.2"/>
              <path d="M28 42a14 14 0 1 0 0-28 14 14 0 0 0 0 28zM54 54l-9.8-9.8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="22" y1="28" x2="34" y2="28" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <h3>No results found for "${query}"</h3>
            <p>Try adjusting your search terms or browse our collections.</p>
          </div>
        `;
        this.resultsContainer.innerHTML = html;
        return;
      }

      // Products section
      if (data.products && data.products.length > 0) {
        html += '<div class="custom-search-section">';
        html += `<h3 class="custom-search-section-title">Products (${data.products.length})</h3>`;
        html += '<div class="custom-search-products">';

        data.products.forEach(product => {
          const image = product.images_info?.[0]?.src || product.images?.['1'] || '';
          const price = product.price_min ? `$${product.price_min.toFixed(2)}` : '';
          const comparePrice = product.compare_at_price_min ? `$${product.compare_at_price_min.toFixed(2)}` : '';
          const onSale = comparePrice && product.compare_at_price_min > product.price_min;

          html += `
            <a href="/products/${product.handle}" class="custom-search-product-item">
              <div class="custom-search-product-image">
                ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` : '<div class="custom-search-product-no-image">No Image</div>'}
                ${!product.available ? '<span class="custom-search-badge sold-out">Sold Out</span>' : ''}
                ${onSale ? '<span class="custom-search-badge sale">Sale</span>' : ''}
              </div>
              <div class="custom-search-product-info">
                <h4 class="custom-search-product-title">${product.title}</h4>
                ${product.product_type ? `<p class="custom-search-product-type">${product.product_type}</p>` : ''}
                <div class="custom-search-product-price">
                  ${onSale ? `<span class="custom-search-price-compare">${comparePrice}</span>` : ''}
                  <span class="custom-search-price">${price}</span>
                </div>
              </div>
            </a>
          `;
        });

        html += '</div></div>';
      }

      // Collections section
      if (data.collections && data.collections.length > 0) {
        html += '<div class="custom-search-section">';
        html += `<h3 class="custom-search-section-title">Collections (${data.collections.length})</h3>`;
        html += '<div class="custom-search-collections">';

        data.collections.forEach(collection => {
          html += `
            <a href="/collections/${collection.handle}" class="custom-search-collection-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M2 3h16M4 3v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3M7 3V1h6v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${collection.title}</span>
            </a>
          `;
        });

        html += '</div></div>';
      }

      // Pages section
      if (data.pages && data.pages.length > 0) {
        html += '<div class="custom-search-section">';
        html += `<h3 class="custom-search-section-title">Pages (${data.pages.length})</h3>`;
        html += '<div class="custom-search-pages">';

        data.pages.forEach(page => {
          html += `
            <a href="/pages/${page.handle}" class="custom-search-page-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 2H6a2 2 0 0 0-2 2v16l4-2 4 2V4a2 2 0 0 0-2-2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${page.title}</span>
            </a>
          `;
        });

        html += '</div></div>';
      }

      // Suggestions section
      if (data.suggestions && data.suggestions.length > 0) {
        html += '<div class="custom-search-section">';
        html += '<h3 class="custom-search-section-title">Search Suggestions</h3>';
        html += '<div class="custom-search-suggestions">';

        data.suggestions.forEach(suggestion => {
          html += `
            <button class="custom-search-suggestion-item" data-query="${suggestion}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7 13A6 6 0 1 0 7 1a6 6 0 0 0 0 12zM15 15l-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${suggestion}</span>
            </button>
          `;
        });

        html += '</div></div>';
      }

      // Did You Mean section
      if (data.did_you_mean && data.did_you_mean.length > 0) {
        html += '<div class="custom-search-section">';
        html += '<h3 class="custom-search-section-title">Did You Mean?</h3>';
        html += '<div class="custom-search-did-you-mean">';

        data.did_you_mean.forEach(dym => {
          html += `
            <button class="custom-search-dym-item" data-query="${dym}">
              ${dym}
            </button>
          `;
        });

        html += '</div></div>';
      }

      this.resultsContainer.innerHTML = html;

      // Attach click handlers for suggestions and did-you-mean
      this.resultsContainer.querySelectorAll('[data-query]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const query = btn.getAttribute('data-query');
          this.searchInput.value = query;
          this.search(query);
        });
      });
    }

    // Show error state
    showError(message) {
      this.resultsContainer.innerHTML = `
        <div class="custom-search-error">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2"/>
            <line x1="32" y1="20" x2="32" y2="36" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <circle cx="32" cy="44" r="2" fill="currentColor"/>
          </svg>
          <h3>Something went wrong</h3>
          <p>${message}</p>
          <button class="custom-search-retry" onclick="window.CustomSearchModal.search(window.CustomSearchModal.searchInput.value)">
            Try Again
          </button>
        </div>
      `;
    }
  }

  // Initialize when DOM is ready
  function initCustomSearchModal() {
    if (window.CustomSearchModal) {
      console.warn('Custom Search Modal already initialized');
      return;
    }

    window.CustomSearchModal = new CustomSearchModal();
    window.CustomSearchModal.init();
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomSearchModal);
  } else {
    initCustomSearchModal();
  }
})();
